import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Download,
  LogOut,
  Loader2,
  Mail,
  Users,
  ClipboardList,
  Send,
  X,
  Briefcase,
  FileText,
} from "lucide-react";
import { toast } from "sonner";
import {
  adminListSubscribers,
  adminListContact,
  adminListCareers,
  adminDownloadCareerCV,
  adminStats,
  adminDownload,
  api,
  getToken,
  clearToken,
} from "@/lib/api";
import { TafraLogo } from "@/components/site/Header";
import ThemeToggle from "@/components/site/ThemeToggle";

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString();
  } catch (e) {
    return iso;
  }
};

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div
    className="relative p-6 btn-sharp overflow-hidden"
    style={{ background: "var(--surface)", border: "1px solid var(--border)" }}
    data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
  >
    <span
      className="absolute -top-px left-6 right-6 h-px"
      style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }}
    />
    <div className="flex items-center justify-between">
      <span
        className="font-display uppercase tracking-[0.25em] text-[0.65rem]"
        style={{ color: "var(--text-faint)" }}
      >
        {label}
      </span>
      <span
        className="w-9 h-9 flex items-center justify-center btn-sharp"
        style={{ color: accent, border: `1px solid ${accent}55` }}
      >
        <Icon className="w-4 h-4" strokeWidth={1.6} />
      </span>
    </div>
    <div
      className="mt-4 font-display text-4xl font-light"
      style={{ color: "var(--text)" }}
    >
      {value}
    </div>
  </div>
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("subscribers");
  const [stats, setStats] = useState({});
  const [subs, setSubs] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [careers, setCareers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [broadcastOpen, setBroadcastOpen] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, sub, en, cr] = await Promise.all([
        adminStats(),
        adminListSubscribers(),
        adminListContact(),
        adminListCareers(),
      ]);
      setStats(s);
      setSubs(sub);
      setEnquiries(en);
      setCareers(cr);
    } catch (err) {
      if (err?.response?.status === 401) {
        clearToken();
        navigate("/admin", { replace: true });
        return;
      }
      toast.error("Failed to load data.");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin", { replace: true });
      return;
    }
    loadAll();
  }, [loadAll, navigate]);

  const onLogout = () => {
    clearToken();
    toast.success("Signed out.");
    navigate("/admin", { replace: true });
  };

  const onDownload = (which) => {
    const map = {
      subscribers: ["/admin/subscribers.csv", "tafra_subscribers.csv"],
      enquiries: ["/admin/contact.csv", "tafra_enquiries.csv"],
      careers: ["/admin/careers.csv", "tafra_careers.csv"],
    };
    const [path, filename] = map[which] || map.subscribers;
    adminDownload(path, filename).catch(() => toast.error("Download failed"));
  };

  return (
    <div
      className="min-h-screen"
      style={{ background: "var(--bg)", color: "var(--text)" }}
    >
      <header
        className="sticky top-0 z-30 backdrop-blur-xl"
        style={{
          background: "var(--surface-glass)",
          borderBottom: "1px solid var(--border)",
        }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/admin/dashboard" className="flex items-center gap-3">
            <TafraLogo size={32} />
            <div className="leading-none">
              <span
                className="font-display text-[14px] tracking-[0.28em] font-medium"
                style={{ color: "var(--text)" }}
              >
                TAFRA
              </span>
              <span
                className="block font-display text-[10px] tracking-[0.4em] mt-1"
                style={{ color: "var(--accent-blue)" }}
              >
                ADMIN
              </span>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <button
              onClick={() => setBroadcastOpen(true)}
              data-testid="open-broadcast-btn"
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2 text-sm btn-sharp"
              style={{
                border: "1px solid var(--accent-blue)",
                color: "var(--accent-blue)",
                background: "var(--accent-blue-soft)",
              }}
            >
              <Send className="w-4 h-4" />
              Broadcast
            </button>
            <Link
              to="/"
              data-testid="view-site-link"
              className="hidden sm:inline-flex items-center px-4 py-2 text-sm btn-sharp"
              style={{
                border: "1px solid var(--border-strong)",
                color: "var(--text)",
              }}
            >
              View site
            </Link>
            <button
              onClick={onLogout}
              data-testid="logout-btn"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm btn-sharp"
              style={{
                border: "1px solid var(--accent-crimson)",
                color: "var(--text)",
                background: "var(--accent-crimson-soft)",
              }}
            >
              <LogOut className="w-4 h-4" />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
        <div>
          <span className="eyebrow">Lead overview</span>
          <h1
            className="mt-3 font-display text-3xl md:text-4xl font-light"
            style={{ color: "var(--text)" }}
          >
            Coming-soon dashboard
          </h1>
          <p
            className="mt-2 text-sm max-w-xl"
            style={{ color: "var(--text-muted)" }}
          >
            Everything captured while the public website is in pre-launch. Export
            anytime — or broadcast a single email to all subscribers when you&apos;re
            ready to go live.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={Users}
            label="Subscribers"
            value={stats.subscribers ?? "—"}
            accent="var(--accent-blue)"
          />
          <StatCard
            icon={Mail}
            label="Contact messages"
            value={stats.contact_enquiries ?? "—"}
            accent="var(--text-muted)"
          />
          <StatCard
            icon={ClipboardList}
            label="Site assessments"
            value={stats.site_assessments ?? "—"}
            accent="var(--accent-crimson-hover)"
          />
          <StatCard
            icon={Briefcase}
            label="Career apps"
            value={stats.careers ?? "—"}
            accent="var(--accent-crimson-hover)"
          />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mt-12"
        >
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div
              className="inline-flex btn-sharp flex-wrap"
              style={{ border: "1px solid var(--border)" }}
            >
              <button
                onClick={() => setTab("subscribers")}
                data-testid="tab-subscribers"
                className={`px-5 py-2.5 text-sm tracking-wide transition-colors ${
                  tab === "subscribers" ? "font-semibold" : ""
                }`}
                style={{
                  background:
                    tab === "subscribers" ? "var(--accent-blue)" : "transparent",
                  color:
                    tab === "subscribers" ? "var(--bg)" : "var(--text-muted)",
                }}
              >
                Subscribers
              </button>
              <button
                onClick={() => setTab("enquiries")}
                data-testid="tab-enquiries"
                className={`px-5 py-2.5 text-sm tracking-wide transition-colors ${
                  tab === "enquiries" ? "font-semibold" : ""
                }`}
                style={{
                  background:
                    tab === "enquiries" ? "var(--accent-crimson)" : "transparent",
                  color:
                    tab === "enquiries" ? "#FFFFFF" : "var(--text-muted)",
                }}
              >
                Enquiries
              </button>
              <button
                onClick={() => setTab("careers")}
                data-testid="tab-careers"
                className={`px-5 py-2.5 text-sm tracking-wide transition-colors ${
                  tab === "careers" ? "font-semibold" : ""
                }`}
                style={{
                  background:
                    tab === "careers" ? "var(--accent-crimson)" : "transparent",
                  color:
                    tab === "careers" ? "#FFFFFF" : "var(--text-muted)",
                }}
              >
                Careers
              </button>
            </div>

            <button
              onClick={() => onDownload(tab)}
              data-testid="download-csv-btn"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm btn-sharp"
              style={{
                border: "1px solid var(--border-strong)",
                color: "var(--text)",
              }}
            >
              <Download className="w-4 h-4" />
              Download CSV
            </button>
          </div>

          <div
            className="mt-6 btn-sharp overflow-hidden"
            style={{ border: "1px solid var(--border)" }}
          >
            {loading ? (
              <div
                className="p-16 flex items-center justify-center"
                style={{ color: "var(--text-faint)" }}
              >
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Loading…
              </div>
            ) : (
              renderTab(tab, { subs, enquiries, careers })
            )}
          </div>
        </motion.div>
      </main>

      <BroadcastModal open={broadcastOpen} onClose={() => setBroadcastOpen(false)} />
    </div>
  );
};

const renderTab = (tab, { subs, enquiries, careers }) => {
  if (tab === "subscribers") return <SubscribersTable rows={subs} />;
  if (tab === "enquiries") return <EnquiriesTable rows={enquiries} />;
  return <CareersTable rows={careers} />;
};

const SubscribersTable = ({ rows }) => {
  if (!rows.length)
    return (
      <div
        className="p-16 text-center"
        style={{ color: "var(--text-faint)" }}
        data-testid="empty-subscribers"
      >
        No subscribers yet.
      </div>
    );
  return (
    <div className="overflow-x-auto" data-testid="subscribers-table">
      <table className="w-full text-sm">
        <thead style={{ background: "var(--surface)" }}>
          <tr
            className="text-left text-[11px] tracking-[0.2em] uppercase"
            style={{ color: "var(--text-faint)" }}
          >
            <th className="px-5 py-3 font-medium">Email</th>
            <th className="px-5 py-3 font-medium">Subscribed at</th>
            <th className="px-5 py-3 font-medium">ID</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.id}
              style={{ borderTop: "1px solid var(--border)" }}
            >
              <td className="px-5 py-3" style={{ color: "var(--text)" }}>
                {r.email}
              </td>
              <td className="px-5 py-3" style={{ color: "var(--text-muted)" }}>
                {fmt(r.created_at)}
              </td>
              <td
                className="px-5 py-3 font-mono-spec text-xs"
                style={{ color: "var(--text-faint)" }}
              >
                {r.id}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const EnquiriesTable = ({ rows }) => {
  if (!rows.length)
    return (
      <div
        className="p-16 text-center"
        style={{ color: "var(--text-faint)" }}
        data-testid="empty-enquiries"
      >
        No enquiries yet.
      </div>
    );
  return (
    <div className="overflow-x-auto" data-testid="enquiries-table">
      <table className="w-full text-sm">
        <thead style={{ background: "var(--surface)" }}>
          <tr
            className="text-left text-[11px] tracking-[0.2em] uppercase"
            style={{ color: "var(--text-faint)" }}
          >
            <th className="px-5 py-3 font-medium">When</th>
            <th className="px-5 py-3 font-medium">Type</th>
            <th className="px-5 py-3 font-medium">Name</th>
            <th className="px-5 py-3 font-medium">Email</th>
            <th className="px-5 py-3 font-medium">Phone</th>
            <th className="px-5 py-3 font-medium">Message</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.id}
              className="align-top"
              style={{ borderTop: "1px solid var(--border)" }}
            >
              <td
                className="px-5 py-3 whitespace-nowrap"
                style={{ color: "var(--text-muted)" }}
              >
                {fmt(r.created_at)}
              </td>
              <td className="px-5 py-3">
                <span
                  className="inline-block px-2 py-0.5 text-[10px] tracking-[0.2em] uppercase btn-sharp"
                  style={{
                    color:
                      r.enquiry_type === "site_assessment"
                        ? "var(--accent-crimson-hover)"
                        : "var(--accent-blue)",
                    background:
                      r.enquiry_type === "site_assessment"
                        ? "var(--accent-crimson-soft)"
                        : "var(--accent-blue-soft)",
                    border: `1px solid ${
                      r.enquiry_type === "site_assessment"
                        ? "var(--accent-crimson)"
                        : "var(--accent-blue)"
                    }`,
                  }}
                >
                  {r.enquiry_type === "site_assessment" ? "Assessment" : "Contact"}
                </span>
              </td>
              <td className="px-5 py-3" style={{ color: "var(--text)" }}>
                {r.name}
              </td>
              <td className="px-5 py-3" style={{ color: "var(--text-muted)" }}>
                <a href={`mailto:${r.email}`}>{r.email}</a>
              </td>
              <td className="px-5 py-3" style={{ color: "var(--text-muted)" }}>
                {r.phone ? <a href={`tel:${r.phone}`}>{r.phone}</a> : "—"}
              </td>
              <td
                className="px-5 py-3 whitespace-pre-wrap max-w-md"
                style={{ color: "var(--text-muted)" }}
              >
                {r.message}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const positionLabel = (row) => {
  if (row.position === "other" && row.position_other) return row.position_other;
  const map = {
    solar_pv_engineer: "Solar PV Engineer",
    mep_engineer: "MEP Engineer",
    electrical_technician: "Electrical Technician",
    project_manager: "Project Manager",
    operations_maintenance: "Operations & Maintenance",
    internship: "Internship",
    other: "Other",
  };
  return map[row.position] || row.position;
};

const CareersTable = ({ rows }) => {
  if (!rows.length)
    return (
      <div
        className="p-16 text-center"
        style={{ color: "var(--text-faint)" }}
        data-testid="empty-careers"
      >
        No career applications yet.
      </div>
    );
  const onDownloadCV = (row) => {
    if (!row.cv_filename) return;
    adminDownloadCareerCV(row.id, row.cv_filename).catch(() =>
      toast.error("CV download failed")
    );
  };
  return (
    <div className="overflow-x-auto" data-testid="careers-table">
      <table className="w-full text-sm">
        <thead style={{ background: "var(--surface)" }}>
          <tr
            className="text-left text-[11px] tracking-[0.2em] uppercase"
            style={{ color: "var(--text-faint)" }}
          >
            <th className="px-5 py-3 font-medium">When</th>
            <th className="px-5 py-3 font-medium">Name</th>
            <th className="px-5 py-3 font-medium">Email</th>
            <th className="px-5 py-3 font-medium">Phone</th>
            <th className="px-5 py-3 font-medium">Role</th>
            <th className="px-5 py-3 font-medium">Message</th>
            <th className="px-5 py-3 font-medium">CV</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.id}
              className="align-top"
              style={{ borderTop: "1px solid var(--border)" }}
            >
              <td
                className="px-5 py-3 whitespace-nowrap"
                style={{ color: "var(--text-muted)" }}
              >
                {fmt(r.created_at)}
              </td>
              <td className="px-5 py-3" style={{ color: "var(--text)" }}>
                {r.name}
              </td>
              <td className="px-5 py-3" style={{ color: "var(--text-muted)" }}>
                <a href={`mailto:${r.email}`}>{r.email}</a>
              </td>
              <td className="px-5 py-3" style={{ color: "var(--text-muted)" }}>
                {r.phone ? <a href={`tel:${r.phone}`}>{r.phone}</a> : "—"}
              </td>
              <td
                className="px-5 py-3"
                style={{ color: "var(--accent-crimson-hover)" }}
              >
                {positionLabel(r)}
              </td>
              <td
                className="px-5 py-3 whitespace-pre-wrap max-w-sm"
                style={{ color: "var(--text-muted)" }}
              >
                {r.message}
              </td>
              <td className="px-5 py-3">
                {r.cv_filename ? (
                  <button
                    onClick={() => onDownloadCV(r)}
                    data-testid={`download-cv-${r.id}`}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-xs btn-sharp"
                    style={{
                      border: "1px solid var(--accent-blue)",
                      color: "var(--accent-blue)",
                      background: "var(--accent-blue-soft)",
                    }}
                  >
                    <FileText className="w-3.5 h-3.5" />
                    {(r.cv_size / 1024).toFixed(0)} KB
                  </button>
                ) : (
                  <span style={{ color: "var(--text-faint)" }}>—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};


const BroadcastModal = ({ open, onClose }) => {
  const [subject, setSubject] = useState("TAFRA Energy — we're live");
  const [html, setHtml] = useState(
    `<p>Hello,</p><p>Our new website is live at <a href="https://www.tafraenergy.com">www.tafraenergy.com</a>. Take a look.</p><p>— TAFRA Energy team</p>`
  );
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const onSend = async (dryRun) => {
    try {
      setLoading(true);
      const t = getToken();
      const res = await api.post(
        "/admin/broadcast",
        { subject, html, dry_run: dryRun },
        { headers: { Authorization: `Bearer ${t}` } }
      );
      if (dryRun) {
        toast.success(
          `Dry run: would send to ${res.data.would_send_to} subscriber(s).`
        );
      } else {
        toast.success(`Broadcast sent: ${res.data.sent} / ${res.data.total}.`);
        onClose();
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Broadcast failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      data-testid="broadcast-modal"
    >
      <div
        className="absolute inset-0 backdrop-blur-md"
        style={{ background: "rgba(0,0,0,0.7)" }}
        onClick={onClose}
      />
      <div
        className="relative w-full max-w-xl btn-sharp p-7"
        style={{
          background: "var(--surface-elev)",
          border: "1px solid var(--border)",
        }}
      >
        <span
          className="absolute -top-px left-10 right-10 h-px"
          style={{
            background:
              "linear-gradient(90deg, transparent, var(--accent-blue), transparent)",
          }}
        />
        <div className="flex items-start justify-between">
          <div>
            <span className="eyebrow">Launch broadcast</span>
            <h3
              className="mt-3 font-display text-xl font-medium"
              style={{ color: "var(--text)" }}
            >
              Send a single email to every subscriber
            </h3>
            <p
              className="mt-2 text-sm"
              style={{ color: "var(--text-muted)" }}
            >
              Use this when your full site goes live. Always run a dry-run first.
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center btn-sharp"
            style={{ border: "1px solid var(--border)", color: "var(--text)" }}
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="mt-6 space-y-4">
          <div>
            <label
              className="block text-[11px] tracking-[0.2em] uppercase mb-2"
              style={{ color: "var(--text-faint)" }}
            >
              Subject
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="broadcast-subject-input"
              className="w-full btn-sharp px-4 py-3 focus:outline-none"
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border)",
                color: "var(--text)",
              }}
            />
          </div>
          <div>
            <label
              className="block text-[11px] tracking-[0.2em] uppercase mb-2"
              style={{ color: "var(--text-faint)" }}
            >
              HTML body
            </label>
            <textarea
              value={html}
              onChange={(e) => setHtml(e.target.value)}
              rows={6}
              data-testid="broadcast-html-input"
              className="w-full font-mono-spec text-xs btn-sharp px-4 py-3 resize-none focus:outline-none"
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border)",
                color: "var(--text)",
              }}
            />
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            onClick={() => onSend(true)}
            disabled={loading}
            data-testid="broadcast-dryrun-btn"
            className="px-5 py-3 text-sm btn-sharp disabled:opacity-60"
            style={{
              border: "1px solid var(--border-strong)",
              color: "var(--text-muted)",
            }}
          >
            Dry run
          </button>
          <button
            onClick={() => onSend(false)}
            disabled={loading}
            data-testid="broadcast-send-btn"
            className="inline-flex items-center gap-2 px-6 py-3 font-semibold btn-sharp transition-all disabled:opacity-60"
            style={{
              background: "var(--accent-crimson)",
              color: "#FFFFFF",
              boxShadow: "0 0 18px var(--accent-crimson-soft)",
            }}
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
            Send to everyone
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
